import React, { useState, useEffect, useRef } from "react";
import { X, Star, Search, Link2, Globe } from "lucide-react";
import {
  createGraphView,
  updateGraphView,
  favoriteGraphView,
  getFilteredGraphViewStats,
} from "../../../../api/graph";
import { ModalInput, ModalActions } from "./GraphModals";
import { ViewPillSection, ViewTypePill } from "./ViewPillSection";
import ColorWheelPicker from "./ColorWheelPicker";
import {
  safeIcon,
  LUCIDE_ICON_ENTRIES,
  POPULAR_ICON_KEYS,
} from "../config/graphIcons";

const DEFAULT_FORM = {
  name: "",
  icon: "sliders-horizontal",
  color: "rgba(14,165,233,0.85)",
  node_types: [],
  edge_types: [],
  keywords: "",
  sources: [],
  target_types: [],
};

const CreateEditViewModal = ({
  t,
  dark,
  initialView,
  connectedTypes,
  graphSources,
  graphTargetTypes,
  onClose,
  onCreated,
  loadGraphViewsData,
}) => {
  const [form, setForm] = useState(() =>
    initialView
      ? {
          name: initialView.name || "",
          icon: initialView.icon || "sliders-horizontal",
          color: initialView.color || "rgba(14,165,233,0.85)",
          node_types: initialView.filters?.node_types || [],
          edge_types: initialView.filters?.edge_types || [],
          keywords: (initialView.filters?.keywords || []).join(", "),
          sources: initialView.filters?.sources || [],
          target_types: initialView.filters?.target_types || [],
        }
      : { ...DEFAULT_FORM },
  );
  const [showFavSuggestion, setShowFavSuggestion] = useState(false);

  const [nodeSearch, setNodeSearch] = useState("");
  const [edgeSearch, setEdgeSearch] = useState("");
  const [sourceSearch, setSourceSearch] = useState("");
  const [targetTypeSearch, setTargetTypeSearch] = useState("");
  const [iconSearch, setIconSearch] = useState("");
  const [modalStats, setModalStats] = useState(null);
  const statsTimer = useRef(null);

  // ── Arwes tokens ──────────────────────────────────────────────────────────
  const cyan = "14,165,233";
  const cyanLit = "56,189,248";

  // Border matches bottom bar / panels exactly
  const cyanBorder = dark ? `rgba(${cyan},0.38)` : `rgba(100,116,139,0.28)`;
  const cyanText = dark ? `rgb(${cyanLit})` : `rgb(${cyan})`;
  const mutedColor = dark ? `rgba(148,163,184,0.65)` : `rgba(71,85,105,0.80)`;
  const divider = dark ? `rgba(${cyan},0.12)` : `rgba(100,116,139,0.15)`;

  // Inner section bg — slightly deeper than the panel surface
  const innerBg = dark ? `rgba(16,24,40,0.55)` : `rgba(213,221,232,0.40)`;
  const innerBorder = dark ? `rgba(${cyan},0.14)` : `rgba(100,116,139,0.22)`;

  // Subtle radial top-glow for the modal itself (dark only)
  const innerGlow = dark
    ? `radial-gradient(ellipse 80% 50% at 50% 0%, rgba(${cyan},0.06) 0%, transparent 100%)`
    : "none";

  // ── Stats debounce ────────────────────────────────────────────────────────
  useEffect(() => {
    if (statsTimer.current) clearTimeout(statsTimer.current);
    const kwList = form.keywords
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean);
    const hasAny =
      form.node_types.length > 0 ||
      form.edge_types.length > 0 ||
      kwList.length > 0 ||
      form.sources.length > 0 ||
      form.target_types.length > 0;
    if (!hasAny) {
      setModalStats(null);
      return;
    }
    statsTimer.current = setTimeout(() => {
      getFilteredGraphViewStats({
        filters: {
          node_types: form.node_types,
          edge_types: form.edge_types,
          keywords: kwList,
          sources: form.sources,
          target_types: form.target_types,
        },
      })
        .then((res) => {
          if (res.data?.status === 200) setModalStats(res.data);
        })
        .catch(() => {});
    }, 400);
    return () => {
      if (statsTimer.current) clearTimeout(statsTimer.current);
    };
  }, [
    form.node_types,
    form.edge_types,
    form.keywords,
    form.sources,
    form.target_types,
  ]);

  const kwList = form.keywords
    .split(",")
    .map((k) => k.trim())
    .filter(Boolean);
  const hasFilters =
    form.node_types.length > 0 ||
    form.edge_types.length > 0 ||
    kwList.length > 0 ||
    form.sources.length > 0 ||
    form.target_types.length > 0;
  const isSingleNode =
    form.node_types.length === 1 &&
    form.edge_types.length === 0 &&
    kwList.length === 0 &&
    form.sources.length === 0 &&
    form.target_types.length === 0;
  const isSingleEdge =
    form.edge_types.length === 1 &&
    form.node_types.length === 0 &&
    kwList.length === 0 &&
    form.sources.length === 0 &&
    form.target_types.length === 0;
  const canSuggestFav = !initialView && (isSingleNode || isSingleEdge);

  const handleSubmit = async () => {
    try {
      const payload = {
        name: form.name,
        icon: form.icon,
        color: form.color,
        filters: {
          scope: "global",
          team_uuid: null,
          node_types: form.node_types,
          edge_types: form.edge_types,
          keywords: kwList,
          sources: form.sources,
          target_types: form.target_types,
        },
      };
      let res;
      if (initialView) {
        payload.document_id = initialView.document_id;
        res = await updateGraphView(payload);
      } else {
        res = await createGraphView(payload);
      }
      if (res.data?.status === 200) {
        onClose();
        loadGraphViewsData();
        window.dispatchEvent(new CustomEvent("sidebar:refresh-graph-views"));
        if (res.data.view && onCreated) onCreated(res.data.view);
      } else if (res.data?.status === 409) {
        alert("A view with that name already exists.");
      }
    } catch (err) {
      console.error(
        initialView ? "Update view failed:" : "Create view failed:",
        err,
      );
    }
  };

  const handleFavInstead = async () => {
    const kind = isSingleNode ? "node_type" : "edge_type";
    const value = isSingleNode ? form.node_types[0] : form.edge_types[0];
    const label = value.replace(/_/g, " ").toLowerCase();
    try {
      await favoriteGraphView({ kind, value, label, meta: {} });
      onClose();
      loadGraphViewsData();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-graph-views"));
    } catch (err) {
      console.error("Favorite failed:", err);
    }
  };

  return (
    <>
      <style>{`
        @keyframes arwes-modal-pulse {
          0%, 100% {
            box-shadow:
              0 0 0 1px rgba(${cyan},0.08),
              0 0 24px 4px rgba(${cyan},0.08),
              0 0 60px 8px rgba(${cyan},0.03);
          }
          50% {
            box-shadow:
              0 0 0 1px rgba(${cyan},0.14),
              0 0 32px 6px rgba(${cyan},0.13),
              0 0 80px 12px rgba(${cyan},0.05);
          }
        }
      `}</style>

      <div
        className={`fixed inset-0 z-[70] flex items-center justify-center ${t.backdrop}`}
        onClick={onClose}
      >
        <div
          className="flex max-h-[85vh] w-[680px] flex-col rounded-lg overflow-hidden backdrop-blur-md"
          style={{
            background:
              innerGlow !== "none"
                ? `${innerGlow}, ${t.surface.bgSolid}`
                : t.surface.bgSolid,
            border: `1px solid ${cyanBorder}`,
            animation: "arwes-modal-pulse 5s ease-in-out infinite",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className="flex shrink-0 items-center justify-between px-5 py-3"
            style={{ borderBottom: `1px solid ${divider}` }}
          >
            <h3 className="text-sm font-medium tracking-wide text-mainText">
              {initialView ? "Edit Graph View" : "Create Graph View"}
            </h3>
            <button
              className={`cursor-pointer rounded-lg p-1 transition ${t.btn.dismissIcon}`}
              onClick={onClose}
            >
              {React.createElement(X, { size: 14 })}
            </button>
          </div>

          <div
            className="flex-1 overflow-y-auto px-5 pb-4 pt-3"
            style={{ scrollbarWidth: "thin" }}
          >
            <ModalInput
              label="View Name"
              placeholder="e.g. APT Infrastructure"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              t={t}
            />

            {/* Icon & Color row */}
            <div className="mb-3 grid grid-cols-2 gap-3">
              {/* Icon picker */}
              <div>
                <label
                  className="mb-1.5 block text-xs font-medium tracking-wide"
                  style={{ color: mutedColor }}
                >
                  Icon
                </label>
                <div className="relative mb-1.5">
                  <Search
                    size={11}
                    style={{
                      position: "absolute",
                      left: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      opacity: 0.4,
                    }}
                  />
                  <input
                    type="text"
                    className={`w-full rounded-lg py-1.5 pl-7 pr-3 text-xs outline-none transition ${t.input.bg} border ${t.input.border} ${t.input.text} ${t.input.placeholder} ${t.input.focusBorder}`}
                    placeholder="Search icons..."
                    value={iconSearch}
                    onChange={(e) => setIconSearch(e.target.value)}
                  />
                </div>
                <div
                  className="flex max-h-[200px] flex-wrap gap-0.5 overflow-y-auto rounded-lg p-1.5"
                  style={{
                    background: innerBg,
                    border: `1px solid ${innerBorder}`,
                    scrollbarWidth: "thin",
                  }}
                >
                  {(() => {
                    const search = iconSearch.toLowerCase().trim();
                    let filtered = search
                      ? LUCIDE_ICON_ENTRIES.filter(
                          (e) =>
                            e.key.includes(search) ||
                            e.name.toLowerCase().includes(search),
                        )
                      : LUCIDE_ICON_ENTRIES.filter((e) =>
                          POPULAR_ICON_KEYS.has(e.key),
                        );
                    const currentKey = form.icon;
                    if (
                      currentKey &&
                      !filtered.some((e) => e.key === currentKey)
                    ) {
                      const current = LUCIDE_ICON_ENTRIES.find(
                        (e) => e.key === currentKey,
                      );
                      if (current) filtered = [current, ...filtered];
                    }
                    return filtered.map((entry) => {
                      const IconComp = entry.component;
                      const isActive = form.icon === entry.key;
                      return (
                        <button
                          key={entry.key}
                          type="button"
                          className="cursor-pointer rounded-lg p-1.5 transition-all"
                          title={entry.name}
                          style={{
                            background: isActive
                              ? `rgba(${cyan},0.12)`
                              : "transparent",
                            border: isActive
                              ? `1px solid rgba(${cyan},0.30)`
                              : "1px solid transparent",
                            boxShadow:
                              isActive && dark
                                ? `0 0 8px rgba(${cyan},0.15)`
                                : "none",
                          }}
                          onClick={() =>
                            setForm((f) => ({ ...f, icon: entry.key }))
                          }
                        >
                          <IconComp
                            size={14}
                            style={{
                              color: isActive ? form.color : mutedColor,
                            }}
                          />
                        </button>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* Color picker */}
              <div>
                <label
                  className="mb-1.5 block text-xs font-medium tracking-wide"
                  style={{ color: mutedColor }}
                >
                  Color
                </label>
                <ColorWheelPicker
                  value={form.color}
                  onChange={(c) => setForm((f) => ({ ...f, color: c }))}
                  dark={dark}
                />
              </div>
            </div>

            {/* 2-column pill grid */}
            <div className="grid grid-cols-2 gap-x-4">
              <ViewPillSection
                t={t}
                dark={dark}
                label="Node Types"
                count={form.node_types.length}
                searchVal={nodeSearch}
                onSearch={setNodeSearch}
                placeholder="Search node types..."
              >
                {connectedTypes
                  .filter((ct) => ct.type === "node")
                  .filter(
                    (ct) =>
                      !nodeSearch ||
                      ct.value.toLowerCase().includes(nodeSearch.toLowerCase()),
                  )
                  .map((ct) => {
                    const IconC = safeIcon(ct.value);
                    const ic =
                      (t.node.typeColors && t.node.typeColors[ct.value]) ||
                      t.node.iconColor;
                    return (
                      <ViewTypePill
                        dark={dark}
                        key={ct.value}
                        active={form.node_types.includes(ct.value)}
                        icon={IconC}
                        iconColor={ic}
                        label={ct.value.replace(/_/g, " ").toLowerCase()}
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            node_types: f.node_types.includes(ct.value)
                              ? f.node_types.filter((nt) => nt !== ct.value)
                              : [...f.node_types, ct.value],
                          }))
                        }
                      />
                    );
                  })}
              </ViewPillSection>

              <ViewPillSection
                t={t}
                dark={dark}
                label="Edge Types"
                count={form.edge_types.length}
                searchVal={edgeSearch}
                onSearch={setEdgeSearch}
                placeholder="Search edge types..."
              >
                {connectedTypes
                  .filter((ct) => ct.type === "link")
                  .filter(
                    (ct) =>
                      !edgeSearch ||
                      ct.value.toLowerCase().includes(edgeSearch.toLowerCase()),
                  )
                  .map((ct) => (
                    <ViewTypePill
                      dark={dark}
                      key={ct.value}
                      active={form.edge_types.includes(ct.value)}
                      icon={Link2}
                      iconColor={mutedColor}
                      label={ct.value.replace(/_/g, " ").toLowerCase()}
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          edge_types: f.edge_types.includes(ct.value)
                            ? f.edge_types.filter((et) => et !== ct.value)
                            : [...f.edge_types, ct.value],
                        }))
                      }
                    />
                  ))}
              </ViewPillSection>

              {graphSources.length > 0 && (
                <ViewPillSection
                  t={t}
                  dark={dark}
                  label="Sources"
                  count={form.sources.length}
                  searchVal={sourceSearch}
                  onSearch={setSourceSearch}
                  placeholder="Search sources..."
                >
                  {graphSources
                    .filter(
                      (s) =>
                        !sourceSearch ||
                        s.toLowerCase().includes(sourceSearch.toLowerCase()),
                    )
                    .map((src) => (
                      <ViewTypePill
                        dark={dark}
                        key={src}
                        active={form.sources.includes(src)}
                        icon={Globe}
                        iconColor={mutedColor}
                        label={src}
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            sources: f.sources.includes(src)
                              ? f.sources.filter((s) => s !== src)
                              : [...f.sources, src],
                          }))
                        }
                      />
                    ))}
                </ViewPillSection>
              )}

              {graphTargetTypes.length > 0 && (
                <ViewPillSection
                  t={t}
                  dark={dark}
                  label="Post Types"
                  count={form.target_types.length}
                  searchVal={targetTypeSearch}
                  onSearch={setTargetTypeSearch}
                  placeholder="Search post types..."
                >
                  {graphTargetTypes
                    .filter(
                      (tt) =>
                        !targetTypeSearch ||
                        tt
                          .toLowerCase()
                          .includes(targetTypeSearch.toLowerCase()),
                    )
                    .map((tt) => (
                      <ViewTypePill
                        dark={dark}
                        key={tt}
                        active={form.target_types.includes(tt)}
                        icon={null}
                        iconColor={null}
                        label={tt.replace(/_/g, " ")}
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            target_types: f.target_types.includes(tt)
                              ? f.target_types.filter((x) => x !== tt)
                              : [...f.target_types, tt],
                          }))
                        }
                      />
                    ))}
                </ViewPillSection>
              )}
            </div>

            <ModalInput
              label="Keywords (comma-separated)"
              placeholder="e.g. cobalt, APT29, malware"
              value={form.keywords}
              onChange={(e) =>
                setForm((f) => ({ ...f, keywords: e.target.value }))
              }
              autoFocus={false}
              t={t}
            />

            {/* Favorite suggestion banner — amber intentional */}
            {canSuggestFav && !showFavSuggestion && (
              <div
                className="mb-3 flex items-center justify-between rounded-lg px-3 py-2 text-xs"
                style={{
                  background: dark
                    ? "rgba(251,191,36,0.06)"
                    : "rgba(251,191,36,0.08)",
                  border: `1px solid ${dark ? "rgba(251,191,36,0.15)" : "rgba(251,191,36,0.20)"}`,
                  color: dark ? "rgb(253,224,71)" : "rgb(161,98,7)",
                }}
              >
                <span>
                  Only one {isSingleNode ? "node type" : "edge type"} selected —
                  would you rather just favorite it?
                </span>
                <div className="ml-3 flex shrink-0 items-center gap-2">
                  <button
                    className="cursor-pointer rounded-lg px-2 py-0.5 text-xs font-medium transition"
                    style={{
                      background: "rgba(251,191,36,0.15)",
                      color: dark ? "rgb(253,224,71)" : "rgb(161,98,7)",
                    }}
                    onClick={handleFavInstead}
                  >
                    {React.createElement(Star, {
                      size: 10,
                      fill: "currentColor",
                      style: {
                        display: "inline",
                        marginRight: 3,
                        verticalAlign: "-1px",
                      },
                    })}
                    Favorite
                  </button>
                  <button
                    className="cursor-pointer rounded-lg px-2 py-0.5 text-xs opacity-60 transition hover:opacity-100"
                    onClick={() => setShowFavSuggestion(true)}
                  >
                    No, create view
                  </button>
                </div>
              </div>
            )}

            {/* Live filter stats */}
            {modalStats && (
              <div
                className="mb-3 flex items-center gap-3 rounded-lg px-3 py-2 text-xs"
                style={{
                  background: innerBg,
                  border: `1px solid ${innerBorder}`,
                  color: mutedColor,
                }}
              >
                <span>{modalStats.post_count.toLocaleString()} posts</span>
                <span style={{ opacity: 0.3 }}>·</span>
                <span>{modalStats.node_count.toLocaleString()} nodes</span>
                <span style={{ opacity: 0.3 }}>·</span>
                <span>{modalStats.edge_count.toLocaleString()} edges</span>
                {modalStats.source_count > 0 && (
                  <>
                    <span style={{ opacity: 0.3 }}>·</span>
                    <span>{modalStats.source_count} sources</span>
                  </>
                )}
                {modalStats.post_type_count > 0 && (
                  <>
                    <span style={{ opacity: 0.3 }}>·</span>
                    <span>{modalStats.post_type_count} post types</span>
                  </>
                )}
              </div>
            )}

            <ModalActions
              onCancel={onClose}
              onConfirm={handleSubmit}
              confirmLabel={initialView ? "Save Changes" : "Create View"}
              disabled={!form.name.trim() || !hasFilters}
              t={t}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateEditViewModal;
